import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { FontAwesome5 } from '@expo/vector-icons';

import WeekTimelineView, { CalendarEvent } from './WeekTimelineView';
import WeekAgendaView from './WeekAgendaView';
import MonthGridView from './MonthGridView';
import WeekGridView from './WeekGridView';

dayjs.extend(isBetween);

type ViewMode = 'agenda' | 'day' | 'week' | 'month';

interface CalendarViewProps {
  events: CalendarEvent[];
  mode?: ViewMode;
  date?: Date;
  weekStartDay?: number; // 0 = Sunday
  startHour?: number;
  endHour?: number;
  onEventPress?: (event: CalendarEvent) => void;
  onCellPress?: (date: Date) => void;
}

const PRIMARY = '#2e96e1';

const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  mode = 'month',
  date,
  weekStartDay = 0,
  startHour = 0,
  endHour = 24,
  onEventPress,
  onCellPress,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(date || new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(mode);

  const handlePrev = () => {
    const d =
      viewMode === 'day' ? dayjs(selectedDate).subtract(1, 'day')
      : viewMode === 'month' ? dayjs(selectedDate).subtract(1, 'month')
      : dayjs(selectedDate).subtract(1, 'week');
    setSelectedDate(d.toDate());
  };
  const handleNext = () => {
    const d =
      viewMode === 'day' ? dayjs(selectedDate).add(1, 'day')
      : viewMode === 'month' ? dayjs(selectedDate).add(1, 'month')
      : dayjs(selectedDate).add(1, 'week');
    setSelectedDate(d.toDate());
  };
  const handleToday = () => setSelectedDate(new Date());

  const headerLabel = useMemo(() => {
    if (viewMode === 'month') return dayjs(selectedDate).format('MMMM YYYY');
    if (viewMode === 'day') return dayjs(selectedDate).format('dddd, MMM D, YYYY');
    return dayjs(selectedDate).format('MMMM YYYY');
  }, [selectedDate, viewMode]);

  const renderBody = () => {
    switch (viewMode) {
      case 'agenda':
        return (
          <WeekAgendaView
            events={events}
            selectedDate={selectedDate}
            weekStartDay={weekStartDay}
            onPressEvent={onEventPress}
            onPressDay={onCellPress}
          />
        );
      case 'day':
        return (
          <WeekTimelineView
            events={events}
            selectedDate={selectedDate}
            numberOfDays={1}
            startHour={startHour}
            endHour={endHour}
            weekStartDay={weekStartDay}
            onPressEvent={onEventPress}
            onPressCell={onCellPress}
          />
        );
      case 'month':
        return (
          <MonthGridView
            events={events}
            selectedDate={selectedDate}
            weekStartDay={weekStartDay}
            onPressEvent={onEventPress}
            onPressDay={onCellPress}
            onShowMore={(d) => {
              setSelectedDate(d);
              setViewMode('day');
            }}
          />
        );
      case 'week':
      default:
        return (
          <WeekGridView
            events={events}
            selectedDate={selectedDate}
            weekStartDay={weekStartDay}
            onPressEvent={onEventPress}
            onPressDay={onCellPress}
          />
        );
    }
  };

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{headerLabel}</Text>
        <View style={styles.headerRight}>
          {(['agenda', 'day', 'week', 'month'] as ViewMode[]).map(m => (
            <TouchableOpacity
              key={m}
              onPress={() => setViewMode(m)}
              style={[styles.modeBtn, viewMode === m && styles.modeBtnActive]}
            >
              <Text style={[styles.modeBtnText, viewMode === m && styles.modeBtnTextActive]}>
                {m}
              </Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity style={styles.iconBtn} onPress={handlePrev}>
            <FontAwesome5 name="chevron-left" size={16} color="#111" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.iconBtn} onPress={handleNext}>
            <FontAwesome5 name="chevron-right" size={16} color="#111" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.todayBtn} onPress={handleToday}>
            <Text style={styles.todayBtnText}>Today</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.body}>
        {renderBody()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, width: '100%', minWidth: 1400, backgroundColor: '#fff' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: '#fff',
  },
  headerTitle: { fontSize: 16, fontWeight: '600', color: '#111' },
  headerRight: { flexDirection: 'row', alignItems: 'center' },
  modeBtn: {
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 4,
    backgroundColor: '#f5f5f5',
    marginRight: 6,
  },
  modeBtnActive: { backgroundColor: PRIMARY },
  modeBtnText: { color: '#111', fontWeight: '400', textTransform: 'capitalize', fontSize: 13 },
  modeBtnTextActive: { color: '#fff', fontWeight: '600' },
  iconBtn: { padding: 8 },
  todayBtn: {
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 4,
    backgroundColor: '#f5f5f5',
    marginLeft: 4,
  },
  todayBtnText: { fontSize: 14, color: '#111' },
  body: { flex: 1, width: '100%', minHeight: 500 },
});

export default CalendarView;
